import { useTranslation } from 'react-i18next';
import JxtButton from '@jooxter/ui/src/components/JxtButton';
import { ButtonVariantEnum } from '@jooxter/ui/src/components/JxtButton/types';
import JxtModal from '@jooxter/ui/src/components/JxtModal';
import JxtRadioButton from '@jooxter/ui/src/components/JxtRadioButton';
import JxtBookingInfo from '@jooxter/ui/src/components/JxtBookingInfo';
import JxtBookingInfoContainer from '@jooxter/ui/src/components/JxtBookingInfoContainer';
import { fromISO, sortBookingsByDate } from '@jooxter/utils';
import { canDeleteBooking, canUpdateBooking, computeTruncatedRange } from '../helpers';
import { IJxtOverlapResolutionModal, OverlapResolutionActionEnum } from '../types';
import { IOverlapBookingChoice } from './types';

const OverlapBookingChoice = ({
  booking,
  newRange,
  selected,
  moveDisabled,
  allowMove,
  newResource,
  onSelect,
  now,
}: IOverlapBookingChoice) => {
  const { t } = useTranslation();

  const existingRange = {
    from: fromISO(booking.start.dateTime, booking.resource?.timezone),
    to: fromISO(booking.end.dateTime, booking.resource?.timezone),
  };
  const truncatedRange = computeTruncatedRange(existingRange, newRange, now);
  const canDelete = canDeleteBooking(booking);
  const canUpdate = canUpdateBooking(booking);
  const groupName = `overlap-resolution-${booking.id}`;

  return (
    <JxtBookingInfoContainer>
      <div className="flex flex-col gap-4">
        <JxtBookingInfo
          title={{ title: booking.summary }}
          location={{ location: booking.resource.name }}
          day={{ day: existingRange.from }}
          hours={{ range: existingRange }}
        />
        <fieldset className="flex flex-col gap-3">
          <JxtRadioButton
            id={`${groupName}-delete`}
            name={groupName}
            label={t<string>('booking-form.overlap-modal.choice.delete')}
            value={OverlapResolutionActionEnum.Delete}
            checked={selected === OverlapResolutionActionEnum.Delete}
            disabled={!canDelete}
            onChange={() => onSelect(OverlapResolutionActionEnum.Delete)}
          />
          <JxtRadioButton
            id={`${groupName}-truncate`}
            name={groupName}
            label={t<string>('booking-form.overlap-modal.choice.truncate')}
            value={OverlapResolutionActionEnum.Truncate}
            checked={selected === OverlapResolutionActionEnum.Truncate}
            disabled={!truncatedRange || !canUpdate}
            onChange={() => onSelect(OverlapResolutionActionEnum.Truncate)}
          />
          {allowMove && newResource && (
            <JxtRadioButton
              id={`${groupName}-move`}
              name={groupName}
              label={t<string>('booking-form.overlap-modal.choice.move', { resourceName: newResource.name })}
              value={OverlapResolutionActionEnum.Move}
              checked={selected === OverlapResolutionActionEnum.Move}
              disabled={moveDisabled || !canUpdate}
              onChange={() => onSelect(OverlapResolutionActionEnum.Move)}
            />
          )}
        </fieldset>
      </div>
    </JxtBookingInfoContainer>
  );
};

const JxtOverlapResolutionModal = ({
  show,
  bookings,
  newResource,
  allowMove,
  newRange,
  actions,
  onChange,
  onConfirm,
  onHide,
  isLoading,
  hasUnresolvableBooking,
  now,
}: IJxtOverlapResolutionModal) => {
  const { t } = useTranslation();

  const setAction = (bookingId: number, action: OverlapResolutionActionEnum) => {
    onChange((previous: Map<number, OverlapResolutionActionEnum>) => {
      const next = new Map(previous);
      next.set(bookingId, action);
      return next;
    });
  };

  const isMoveDisabledFor = (bookingId: number) =>
    [...actions.entries()].some(([id, action]) => id !== bookingId && action === OverlapResolutionActionEnum.Move);

  return (
    <JxtModal
      header={{ title: t('manage-closing.action-required-from-you'), icon: 'cog' }}
      footer={
        <>
          <JxtButton variant={ButtonVariantEnum.Secondary} onClick={onHide}>
            {t('cancel')}
          </JxtButton>
          <JxtButton onClick={onConfirm} disabled={isLoading || hasUnresolvableBooking}>
            {t('confirm')}
          </JxtButton>
        </>
      }
      show={show}
      onHide={onHide}
    >
      <div className="flex flex-col gap-4">
        <p className="text-title-m text-neutral-140 font-medium">{t('booking-form.overlap-modal.title')}</p>
        {sortBookingsByDate(bookings).map((booking) => (
          <OverlapBookingChoice
            key={booking.id}
            booking={booking}
            newRange={newRange}
            selected={actions.get(booking.id) ?? OverlapResolutionActionEnum.Delete}
            moveDisabled={isMoveDisabledFor(booking.id)}
            allowMove={allowMove}
            newResource={newResource}
            onSelect={(action) => setAction(booking.id, action)}
            now={now}
          />
        ))}
      </div>
    </JxtModal>
  );
};

export default JxtOverlapResolutionModal;
